import React from "react";
import { Helmet } from "react-helmet-async";

/* ================================================================== */
/*  <Seo />                                                             */
/*                                                                     */
/*  Central head-tag + JSON-LD structured data helper for stealth      */
/*  SEO pages. Fills title, meta description, canonical, Open Graph,   */
/*  Twitter card, and injects one or more JSON-LD blocks so AI search  */
/*  engines (Perplexity, ChatGPT, Google AI Overview) can parse.       */
/* ================================================================== */

export function Seo({
  title,
  description,
  canonical,
  keywords = [],
  ogImage,
  jsonLd = [],
  noindex = false,
}) {
  const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  return (
    <Helmet prioritizeSeoTags>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content="Get Virtual Growth" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {/* JSON-LD blocks */}
      {blocks.map((block, i) =>
        block ? (
          <script key={i} type="application/ld+json">
            {JSON.stringify(block)}
          </script>
        ) : null
      )}
    </Helmet>
  );
}

/* ------------------------------------------------------------------ */
/*  Structured data builders                                           */
/* ------------------------------------------------------------------ */

export function organizationSchema(entity) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: entity.name,
    legalName: entity.legalName,
    url: entity.url,
    logo: entity.logo,
    description: entity.description,
    address: entity.address
      ? {
          "@type": "PostalAddress",
          streetAddress: entity.address.streetAddress,
          addressLocality: entity.address.addressLocality,
          addressRegion: entity.address.addressRegion,
          addressCountry: entity.address.addressCountry,
        }
      : undefined,
    areaServed: (entity.areaServed || []).map((a) => ({
      "@type": "Country",
      name: a.name,
    })),
    founder: entity.founderName
      ? { "@type": "Person", name: entity.founderName }
      : undefined,
    sameAs: entity.sameAs,
  };
}

export function serviceSchema(entity, service, cityName) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.serviceType,
    name: cityName ? `${service.name} in ${cityName}` : service.name,
    description: service.description,
    category: service.category,
    provider: {
      "@type": "Organization",
      name: entity.name,
      url: entity.url,
    },
    areaServed: cityName
      ? { "@type": "City", name: cityName }
      : (entity.areaServed || []).map((a) => ({ "@type": "Country", name: a.name })),
  };
}

export function localBusinessSchema(entity, city) {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: `${entity.name} . ${city.name}`,
    description: `Personalised video customer acquisition for firms in ${city.name}, ${city.country}.`,
    url: `${entity.url}/growth/${city.slug}`,
    telephone: "",
    priceRange: "$$$$",
    address: entity.address
      ? {
          "@type": "PostalAddress",
          streetAddress: entity.address.streetAddress,
          addressLocality: entity.address.addressLocality,
          addressRegion: entity.address.addressRegion,
          addressCountry: entity.address.addressCountry,
        }
      : undefined,
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.lat,
      longitude: city.lng,
    },
    areaServed: { "@type": "City", name: city.name },
    parentOrganization: {
      "@type": "Organization",
      name: entity.legalName,
    },
  };
}

export function faqPageSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function articleSchema({ entity, headline, description, url, datePublished }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    author: { "@type": "Person", name: entity.founderName },
    publisher: {
      "@type": "Organization",
      name: entity.name,
      logo: { "@type": "ImageObject", url: entity.logo },
    },
    mainEntityOfPage: url,
    datePublished: datePublished || "2026-02-01",
  };
}
